import * as THREE from "three";
import { buildIkChainFromScene } from "../chainBuilder";
import { resolveKeepDownLocalAxes } from "../orientationTargets";
import { getPhysicsWorker } from "../../physics/workerHandle";
import { usePhysicsStore } from "../../stores/physicsStore";
import { getSceneState } from "../../stores/sceneStore";
import type { BodyUUID } from "../../types/scene";
import type { IkLink } from "../types";
import {
  resolveElbowLinkIndex,
  resolveShoulderLiftLinkIndex,
  resolveShoulderPanLinkIndex,
} from "./chainHeuristics";
import { findGripperTargetOffset } from "./gripperTargetOffset";
import {
  getActiveServoSessionKey,
  setActiveServoSessionKey,
  stopActiveServoSession,
} from "./servoSession";
import { resolveTopmostAncestorBodyUuid } from "./spatialUtils";
import { logServoConfig } from "./pickPlaceServoDebug";

export type PickPlaceServoStartArgs = {
  targetBodyUuid: BodyUUID;
  endEffectorUuid: BodyUUID;
  useGripperTargetSite: boolean;
  dropoffBodyUuid?: BodyUUID | null;
};

export function createPickPlaceServoSetup(args: PickPlaceServoStartArgs) {
  const scene = getSceneState();
  const { targetBodyUuid, endEffectorUuid, useGripperTargetSite, dropoffBodyUuid = null } = args;
  const physicsState = usePhysicsStore.getState();
  const isPhysicsRunning = physicsState.isRunning === true;
  const physicsWorker: Worker | null = getPhysicsWorker();
  if (!isPhysicsRunning || !physicsWorker) return null;

  // Avoid restarting the servo session if an identical request is spammed (e.g. repeated event dispatch).
  // Restarts reset the pick/place stage machine and can manifest as persistent "jitter loops".
  const sessionKey = [
    String(targetBodyUuid || ""),
    String(endEffectorUuid || ""),
    useGripperTargetSite ? "1" : "0",
    String(dropoffBodyUuid || ""),
  ].join("|");
  if (getActiveServoSessionKey() === sessionKey) return null;

  stopActiveServoSession();
  setActiveServoSessionKey(sessionKey);

  const yToZ = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2, 0, 0));
  const zToY = yToZ.clone().invert();

  // Build IK chain rooted at the topmost ancestor of the end-effector (robot base),
  // not necessarily `worldBodies[0]` (which can be unrelated after merges).
  const chainRoot = resolveTopmostAncestorBodyUuid(scene, endEffectorUuid);
  const chain = buildIkChainFromScene(scene, endEffectorUuid, chainRoot || undefined);
  if (!chain) return null;

  const dofLinks = chain.links
    .filter(
      (l): l is IkLink =>
        (l.kind === "hinge" || l.kind === "slide") && !!l.jointUuid && l.dofIndex !== undefined
    )
    .sort((a, b) => (a.dofIndex ?? 0) - (b.dofIndex ?? 0));

  // Used for continuity scoring when multiple IK solutions exist.
  const dofKindByIndex = new Map<number, "hinge" | "slide">();
  const dofLimitByIndex = new Map<number, [number, number]>();
  dofLinks.forEach((l) => {
    if (l.dofIndex == null) return;
    if (l.kind === "hinge" || l.kind === "slide") dofKindByIndex.set(l.dofIndex, l.kind);
    if (
      Array.isArray(l.limit) &&
      l.limit.length === 2 &&
      Number.isFinite(l.limit[0]) &&
      Number.isFinite(l.limit[1])
    ) {
      dofLimitByIndex.set(l.dofIndex, [Number(l.limit[0]), Number(l.limit[1])]);
    }
  });

  const targetOffset = useGripperTargetSite
    ? findGripperTargetOffset(scene, endEffectorUuid, yToZ, false)
    : null;
  if (targetOffset) {
    chain.endEffectorOffset = [
      targetOffset.offsetZFrame.x,
      targetOffset.offsetZFrame.y,
      targetOffset.offsetZFrame.z,
    ];
  }
  logServoConfig({
    useGripperTargetSite,
    targetBodyUuid,
    endEffectorUuid,
    dropoffBodyUuid,
    targetOffset,
  });

  const { forwardLocal: gripperForwardLocal, upLocal: gripperUpLocal } =
    resolveKeepDownLocalAxes(targetOffset);

  // Optional preference: when multiple IK solutions exist, prefer ones that keep the elbow up.
  // This helps avoid elbow-down solutions that drag into the ground for targets "behind" the robot.
  const elbowLinkIdx = resolveElbowLinkIndex({ scene, chain });
  const elbowDofIndex =
    elbowLinkIdx != null && Number.isFinite(Number(chain.links?.[elbowLinkIdx]?.dofIndex))
      ? (chain.links[elbowLinkIdx].dofIndex as number)
      : null;

  // Optional preference: prefer mid-range shoulder lift joint values (avoid extremes).
  // This helps keep the elbow from swinging down into the ground for behind-the-robot targets.
  const shoulderLiftLinkIdx = resolveShoulderLiftLinkIndex({ scene, chain });
  const shoulderLiftDofIndex =
    shoulderLiftLinkIdx != null &&
    Number.isFinite(Number(chain.links?.[shoulderLiftLinkIdx]?.dofIndex))
      ? (chain.links[shoulderLiftLinkIdx].dofIndex as number)
      : null;

  const shoulderPanLinkIdx = resolveShoulderPanLinkIndex({ scene, chain });
  const shoulderPanDofIndex =
    shoulderPanLinkIdx != null &&
    Number.isFinite(Number(chain.links?.[shoulderPanLinkIdx]?.dofIndex))
      ? (chain.links[shoulderPanLinkIdx].dofIndex as number)
      : null;

  return {
    scene,
    targetBodyUuid,
    endEffectorUuid,
    useGripperTargetSite,
    dropoffBodyUuid,
    physicsWorker,
    yToZ,
    zToY,
    chain,
    dofLinks,
    dofKindByIndex,
    dofLimitByIndex,
    gripperForwardLocal,
    gripperUpLocal,
    elbowLinkIdx,
    elbowDofIndex,
    shoulderLiftDofIndex,
    shoulderPanLinkIdx,
    shoulderPanDofIndex,
  };
}
